import { BsCashCoin, BsFillPinMapFill } from "react-icons/bs";
import { FaCalendarAlt, FaFileAlt, FaUserTie, FaWineBottle } from 'react-icons/fa';
import { FaArrowsUpDown } from 'react-icons/fa6';
import { LuGrape } from "react-icons/lu";
import { useLanguage } from '../contexts/LanguageContext';
import { WineMaker } from '../utils/constants';
import { useRef, useEffect } from "react";


function WineDetail({ wine, onClose }) {
  const { language } = useLanguage();

  const imgRef = useRef(null);
  const lensRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const img = imgRef.current;
    const lens = lensRef.current;
    const container = containerRef.current;
    
    if (!img || !lens || !container) return;

    const isDesktop = window.matchMedia("(pointer: fine)").matches;
    if (!isDesktop) {
      // ensure lens stays hidden on mobile
      if (lensRef.current) lensRef.current.style.display = "none";
      return;
    }

    const zoom = 3; // magnification factor

    // helper to update lens background size based on displayed image size
    const updateLensBackground = () => {
      const rect = img.getBoundingClientRect();
      // background size should be displayed width * zoom, displayed height * zoom
      lens.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
      lens.style.backgroundImage = `url(${img.src})`;
      lens.style.backgroundRepeat = "no-repeat";
    };

    // compute and position lens
    const moveLens = (e) => {
      // pointer coordinates
      const rect = img.getBoundingClientRect();
      let x = e.clientX - rect.left; // x within image
      let y = e.clientY - rect.top;  // y within image

      const half = lens.offsetWidth / 2;

      // clamp so lens doesn't go outside image
      x = Math.max(half, Math.min(rect.width - half, x));
      y = Math.max(half, Math.min(rect.height - half, y));

      // lens position relative to container (container is positioned)
      const lensLeft = x - half;
      const lensTop = y - half;

      lens.style.left = `${lensLeft}px`;
      lens.style.top = `${lensTop}px`;

      // background-position (shift of the zoomed image inside lens)
      // we subtract half so the zoomed point is centered in the lens
      lens.style.backgroundPosition = `-${(x * zoom) - half}px -${(y * zoom) - half}px`;
    };

    const show = () => (lens.style.display = "block");
    const hide = () => (lens.style.display = "none");

    // Ensure lens background is correct once the image is loaded/displayed
    const handleLoad = () => {
      updateLensBackground();
    };

    // update on resize (window or container changes)
    const handleResize = () => updateLensBackground();

    // attach events
    img.addEventListener("load", handleLoad);
    window.addEventListener("resize", handleResize);

    img.addEventListener("mousemove", moveLens);
    container.addEventListener("mousemove", moveLens); // also on container for safety
    img.addEventListener("mouseenter", show);
    img.addEventListener("mouseleave", hide);
    container.addEventListener("mouseenter", show);
    container.addEventListener("mouseleave", hide);

    // If the image is already cached and complete
    if (img.complete) handleLoad();

    // cleanup
    return () => {
      img.removeEventListener("load", handleLoad);
      window.removeEventListener("resize", handleResize);

      img.removeEventListener("mousemove", moveLens);
      container.removeEventListener("mousemove", moveLens);
      img.removeEventListener("mouseenter", show);
      img.removeEventListener("mouseleave", hide);
      container.removeEventListener("mouseenter", show);
      container.removeEventListener("mouseleave", hide);
    };
  }, [wine.image]);


  const renderLabel = (pt, en) => {
    return language === 'pt' ? pt : en;
  }

  const winemaker = Object.values(WineMaker).find(values => values.value === wine.producer.value)?.label[language];

  return (
    <div className="wine-detail">
      <button className="close-button" onClick={onClose}>&times;</button>
      <div className="wine-detail-content">
        <div className="wine-detail-image-container" ref={containerRef}>
          <img
            ref={imgRef}
            src={wine.image}
            alt={wine.name}
            className="wine-detail-image"
          />
          <div ref={lensRef} className="zoom-lens" />
        </div>
        {/* <div className="wine-detail-image-container zoom-container">
          <img src={wine.image} alt={wine.name} className="wine-detail-image zoom-image" />
        </div> */}
        <div className="wine-detail-info">
          <h2>{wine.name}</h2>
          <p>{wine.producer.label[language]}</p>

          <div className="wine-detail-table">
            

            {wine.year && (
              <div className="wine-detail-row">
                <div className="wine-detail-label">
                  <FaCalendarAlt className="wine-icon" /> <b>{renderLabel("Ano", "Year")}:</b>
                </div>
                <div className="wine-detail-value">{wine.year}</div>
              </div>
            )}

            <div className="wine-detail-row">
              <div className="wine-detail-label">
                <FaWineBottle className="wine-icon" /> <b>{renderLabel("Tipo", "Type")}:</b>
              </div>
              <div className="wine-detail-value">{wine.type.label[language]}</div>
            </div>

            <div className="wine-detail-row">
              <div className="wine-detail-label">
                <BsFillPinMapFill className="wine-icon" /> <b>{renderLabel("Região", "Region")}:</b>
              </div>
              <div className="wine-detail-value">{wine.region.label[language]}</div>
            </div>
            
            <div className="wine-detail-row">
              <div className="wine-detail-label">
                <BsCashCoin className="wine-icon" /> <b>{renderLabel("Preço", "Price")}:</b>
              </div>
              <div className="wine-detail-value">{wine.priceRange.label[language]}</div>
            </div>

            <div className="wine-detail-row">
              <div className="wine-detail-label">
                <FaArrowsUpDown className="wine-icon" /> <b>{renderLabel("Tamanho", "Size")}:</b>
              </div>
              <div className="wine-detail-value">{wine.size.label[language]}</div>
            </div>

            {wine.grapeVarieties && (
              <div className="wine-detail-row">
                <div className="wine-detail-label">
                  <LuGrape className="wine-icon" /> <b>{renderLabel("Castas", "Grapes")}:</b>
                </div>
                <div className="wine-detail-value">{wine.grapeVarieties?.join(', ')}</div>
              </div>
            )}

            {winemaker && (
              <div className="wine-detail-row">
                <div className="wine-detail-label">
                  <FaUserTie className="wine-icon" /> <b>{renderLabel("Enólogo", "Winemaker")}:</b>
                </div>
                <div className="wine-detail-value">
                  {winemaker}
                </div>
              </div>
            )}

            {wine.sheet && (
              <div className="wine-detail-row">
                <div className="wine-detail-label">
                  <FaFileAlt className="wine-icon" /> <b>{renderLabel("Ficha Técnica", "Technical Sheet")}:</b>
                </div>
                <div className="wine-detail-value">
                  <a href={wine.sheet} target="_blank" rel="noreferrer">{language === 'en' ? 'Open' : 'Consultar'}</a>
                </div>
              </div>
            )}
          </div>
          {/* Add more details as needed */}
        </div>
      </div>
    </div>
  );
}

export default WineDetail;


